import React from 'react';
import {
  TableCell,
  Typography,
  Box,
  useTheme,
  TableCellProps,
} from '@mui/material';
import { UnfoldMore as UnsortedIcon } from '@mui/icons-material';
import { SortDirection } from '../hooks/useSorting';
import { getFlatPortfolioColors, portfolioTypography } from '../../../utils/getFlatPortfolioColors';

export interface SortableColumnHeaderProps extends Omit<TableCellProps, 'onClick'> {
  label: string;
  sortKey: string;
  currentSortDirection: SortDirection | null;
  onSort: (sortKey: string) => void;
  sortable?: boolean;
}

// The order drawn as a little bar chart: the longest bar on top is highest
// first, at the bottom lowest first. Arrows read either way round (does "up"
// mean ascending, or biggest on top?), so they were replaced (Ben, 2026-09-28).
const SortBars = ({ highFirst, color }: { highFirst: boolean; color: string }) => (
  <Box component="svg" viewBox="0 0 16 16" aria-hidden sx={{ width: 16, height: 16, ml: 0.5, flex: 'none', color }}>
    {(highFirst ? [12, 8, 4] : [4, 8, 12]).map((width, index) => (
      <rect key={width} x={2} y={3 + index * 4} width={width} height={2} rx={1} fill="currentColor" />
    ))}
  </Box>
);

const SortableColumnHeader: React.FC<SortableColumnHeaderProps> = ({
  label,
  sortKey,
  currentSortDirection,
  onSort,
  sortable = true,
  sx,
  ...props
}) => {
  const theme = useTheme();
  const colors = getFlatPortfolioColors(theme);

  const handleClick = () => {
    if (sortable) {
      onSort(sortKey);
    }
  };

  const getSortIcon = () => {
    if (!sortable) return null;

    switch (currentSortDirection) {
      case 'asc':
        return <SortBars highFirst={false} color={colors.functional.success} />;
      case 'desc':
        return <SortBars highFirst color={colors.functional.success} />;
      default:
        return (
          <UnsortedIcon
            sx={{
              fontSize: 16,
              color: colors.text.muted,
              ml: 0.5,
              opacity: 0.6,
            }}
          />
        );
    }
  };

  // Determine justification based on alignment
  const getJustification = () => {
    if (props.align === 'right') return 'flex-end';
    if (props.align === 'center') return 'center';
    return 'flex-start';
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!sortable || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onSort(sortKey);
  };

  let ariaSort: TableCellProps['aria-sort'];
  if (sortable && currentSortDirection === 'asc') ariaSort = 'ascending';
  if (sortable && currentSortDirection === 'desc') ariaSort = 'descending';

  return (
    <TableCell
      {...props}
      tabIndex={sortable ? 0 : undefined}
      aria-sort={ariaSort}
      onKeyDown={handleKeyDown}
      sx={{
        py: 1.5,
        px: 2,
        borderBottom: 'none',
        cursor: sortable ? 'pointer' : 'default',
        userSelect: 'none',
        transition: 'background-color 0.2s ease',

        '&:hover': sortable ? {
          backgroundColor: colors.background.tertiary,
        } : {},

        ...sx,
      }}
      onClick={handleClick}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: getJustification(),
        }}
      >
        <Typography
          sx={{
            ...portfolioTypography.tableHeader,
            color: currentSortDirection ? colors.text.primary : colors.text.secondary,
            fontWeight: currentSortDirection ? 600 : 500,
            transition: 'color 0.2s ease',
          }}
        >
          {label}
        </Typography>

        {getSortIcon()}
      </Box>
    </TableCell>
  );
};

export default SortableColumnHeader;
